import { ArrowUpRight, Info } from 'lucide-react';
import { useEffect } from 'react';
import { CircleFlag } from 'react-circle-flags';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { ContentLayout } from '@/components/layouts';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ListGroup, ListGroupItem } from '@/components/ui/list-group';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { MEDIA_URL } from '@/config';
import { MatchHistory } from '@/features/teams/components/MatchHistory';
import { useUser } from '@/features/users/api/getUser';
import { UserActions } from '@/features/users/components/UserActions';
import { UserBadges } from '@/features/users/components/UserBadges';
import UserInfoList from '@/features/users/components/UserInfoList';
import { UserSocials } from '@/features/users/components/UserSocials';
import { UserStats } from '@/features/users/components/UserStats';
import UserStatusBox from '@/features/users/components/UserStatusBox';
import { UserTeams } from '@/features/users/components/UserTeams';
import { UserAvatar } from '@/global/auth/components/UserAvatar';
import { useSession } from '@/hooks/useSession';

const ProfileRoute = () => {
  const { data: session } = useSession();
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: user, isLoading: isUserLoading } = useUser({
    userId: id || '',
    queryConfig: {
      enabled: !!id,
    },
  });

  useEffect(() => {
    if (!user && !isUserLoading) {
      navigate('/');
    }
  }, [user, isUserLoading, navigate]);

  if (!id) {
    toast.error('Invalid user ID');
    navigate('/');
    return <Spinner size="lg" className="mx-auto" />;
  }

  // If the ID is a steamid (numeric), redirect to the UUID-based URL
  if (/^\d+$/.test(id) && user?.id) {
    navigate(`/profile/${user.id}`, { replace: true });
    return <Spinner size="lg" className="mx-auto" />;
  }

  // Check if the current user is viewing their own profile
  const isOwnProfile = session?.user?.id === user?.id;

  if (!user) {
    return <Spinner size="lg" className="mx-auto" />;
  }

  console.log(user);

  return (
    <ContentLayout
      title={`${user?.username}`}
      description="A player profile on SCL."
    >
      <div className="shadow">
        <div className="relative h-[349px] w-full overflow-hidden rounded-t-xl border-x border-t border-border">
          {isUserLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <img
              className="grid h-full w-full place-items-center object-cover"
              src={`${MEDIA_URL}/images/uploads/${user?.coverPhoto}`}
              alt="Banner"
            />
          )}
        </div>
        <div className="w-full border-x border-b border-border bg-card p-4 !pb-6 !pt-0 sm:p-8">
          <div className="relative grid sm:flex sm:items-center sm:gap-x-6">
            {isUserLoading ? (
              <Skeleton className="ring-block z-10 mx-auto -mt-16 h-32 w-32 !rounded-full !bg-muted text-xl ring-4 sm:mx-0" />
            ) : (
              <UserAvatar
                className="ring-block z-10 mx-auto -mt-16 h-32 w-32 bg-muted text-4xl ring-4 sm:mx-0"
                user={user}
              />
            )}
            <div className="mt-4 flex items-center justify-center gap-2 sm:mb-1.5 sm:mt-auto sm:justify-start">
              {isUserLoading ? (
                <Skeleton className="h-6 w-96" />
              ) : (
                <>
                  <CircleFlag
                    countryCode={user?.country?.toLocaleLowerCase() || ''}
                    className="size-6"
                  />
                  <h2 className="whitespace-nowrap text-3xl font-semibold ">
                    {user?.username}
                  </h2>
                </>
              )}
            </div>
            {user && (
              <div className="ml-[1%] mt-8 flex w-full justify-between gap-4 sm:mt-4 sm:justify-end">
                <UserBadges userData={user} />
                <UserSocials userData={user} />
                <Separator orientation="vertical" className="h-10" />
                <UserActions userData={user} />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-8 grid gap-y-8 lg:grid-cols-10 lg:gap-x-12 lg:gap-y-0 2xl:gap-x-24">
        <aside className="lg:col-span-3 lg:h-full lg:w-full">
          <div className="flex flex-col gap-y-4">
            <UserStatusBox steamid={user?.steamid} username={user?.username} />
            {isUserLoading ? (
              <ListGroup>
                <ListGroupItem>
                  <Skeleton className="h-2.5 w-full"></Skeleton>
                </ListGroupItem>
                <ListGroupItem>
                  <Skeleton className="h-2.5 w-full"></Skeleton>
                </ListGroupItem>
                <ListGroupItem>
                  <Skeleton className="h-2.5 w-full"></Skeleton>
                </ListGroupItem>
              </ListGroup>
            ) : (
              <>{user && <UserInfoList userData={user} />}</>
            )}
          </div>
        </aside>
        <div className="flex flex-col space-y-8 lg:col-span-7">
          <UserStats steamid={user?.steamid} />
          <UserTeams userId={id} />
          {isOwnProfile && (
            <Link to="/analytics">
              <Card className="relative flex p-4 transition-colors hover:bg-[#111921]">
                <Info className="mt-0.5 size-5 text-[#5865F2]" />
                <CardHeader className="ms-3 p-0">
                  <CardTitle className="text-base">
                    Looking for your Scrim History?
                  </CardTitle>
                  <CardDescription className="max-w-lg text-balance text-xs leading-relaxed">
                    Head over to the scrim analytics page to view your last
                    scrims!
                  </CardDescription>
                </CardHeader>
                <ArrowUpRight
                  className="absolute right-2 top-2 size-5"
                  strokeWidth={1}
                />
              </Card>
            </Link>
          )}
          <MatchHistory userId={id} />
        </div>
      </div>
    </ContentLayout>
  );
};

export default ProfileRoute;
